import { useEffect, useMemo, useState } from "react";
import {
	FormControl,
	FormControlLabel,
	IconButton,
	Radio,
	RadioGroup,
} from "@mui/material";
import { useFormsContext } from "../context/formsContext";
import Grid2 from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import Popover from "@mui/material/Popover";
import CommentIcon from "@mui/icons-material/Comment";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles({
	root: {
		"&:hover": {
			backgroundColor: "rgba(33, 150, 243, 0.08)",
			borderRadius: "2px",
			cursor: "pointer",
		},
	},
	optionLabel: {
		fontSize: "0.75rem",
		padding: "5px",
		borderRadius: "5px",
		overflow: "hidden",
		whiteSpace: "nowrap",
		display: "block",
	},
	radioGroup: {
		width: "100%",
		display: "flex",
		justifyContent: "flex-start",
		flexWrap: "nowrap",
		overflowX: "auto",
	},
	questionText: {
		fontSize: "0.85rem",
		whiteSpace: "normal",
		wordBreak: "break-word",
	},
	questionTextRequired: {
		fontWeight: "bold",
	},
	formControlLabel: {
		marginRight: "8px",
	},
	optionsContainer: {
		display: "flex",
		alignItems: "center",
		paddingRight: "16px",
	},
	commentField: {
		"& .MuiInputBase-input": {
			color: "#222",
		},
	},
});

import type { FormInstanceRowExtended } from "../models/FormInstanceRowExtended";

interface RowQuestion {
	question: [string, FormInstanceRowExtended[]];
}

interface QuestionData {
	QuestionId: number;
	RadioButtonSelected: number | null;
	Comments: string;
	IsRequired: boolean;
	QuestionText: string;
	OptionId: number;
	OptionText: string;
	OptionTypeCode: string;
	IsEnabled: boolean;
}

interface FormInstanceRow {
	QuestionId: number;
	OptionId: number;
	RadioButtonSelected: number;
	Comments: string;
}

export const RadioQuestion: React.FC<RowQuestion> = (rowQuestion) => {
	const [selectedOption, setSelectedOption] = useState<number | null>(null);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [comment, setComment] = useState<string>("");
	const classes = useStyles();
	const {
		state: { formInstanceRows, formInstanceModifiedRows, formInstanceStatus },
		actions: { setFormInstanceModifiedRows },
	} = useFormsContext();

	const getOptionStyle = (optionTypeCode: string, isSelected: boolean) => {
		if (isSelected) {
			switch (optionTypeCode) {
				case "W":
					return {
						backgroundColor: "#FF7F50",
						color: "#FFF",
					};
				case "A":
					return {
						backgroundColor: "#FD2D00",
						color: "#FFF",
					};
				default:
					return {
						backgroundColor: "initial",
						color: "initial",
					};
			}
		} else {
			switch (optionTypeCode) {
				case "W":
					return {
						backgroundColor: "#FFF",
						color: "#FF7F50",
					};
				case "A":
					return {
						backgroundColor: "#FFF",
						color: "#FD2D00",
					};
				default:
					return {
						backgroundColor: "initial",
						color: "initial",
					};
			}
		}
	};

	const handleOptionChange = (optionId: string) => {
		setSelectedOption(Number.parseInt(optionId));
		const questionId = rowQuestion.question[1][0].QuestionId;
		const newOptionId = Number.parseInt(optionId);
		const newModifiedRows = [...formInstanceModifiedRows];
		let comment: string | null = null;
		let newInstance = filterInstance(questionId, newOptionId);

		const existingIndex = formInstanceModifiedRows.findIndex(
			(instance) => instance.QuestionId === questionId,
		);

		if (existingIndex !== -1) {
			comment =
				formInstanceModifiedRows[existingIndex].Comments === ""
					? null
					: formInstanceModifiedRows[existingIndex].Comments;
			newModifiedRows.splice(existingIndex, 1);
		} else {
			comment = newInstance[0].Comments === "" ? null : newInstance[0].Comments;
		}

		newInstance = [
			{
				...newInstance[0],
				Comments: comment as string,
			},
		];
		setFormInstanceModifiedRows([...newModifiedRows, ...newInstance]);
	};

	const filterInstance = (
		questionId: number,
		optionId: number,
	): FormInstanceRow[] => {
		return formInstanceRows.filter(
			(row) => row.QuestionId === questionId && row.OptionId === optionId,
		);
	};

	useEffect(() => {
		setSelectedOption(rowQuestion.question[1][0].RadioButtonSelected);
	}, [rowQuestion.question[1]]);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const filterInstanceByQuestionId = (
		questionId: number,
	): FormInstanceRow[] => {
		return formInstanceRows.filter(
			(row) =>
				row.QuestionId === questionId &&
				row.OptionId === row.RadioButtonSelected,
		);
	};

	const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setComment(event.target.value);

		const questionId = rowQuestion.question[1][0].QuestionId;

		const newModifiedRows = [...formInstanceModifiedRows];

		const existingIndex = newModifiedRows.findIndex(
			(instance) => instance.QuestionId === questionId,
		);

		if (existingIndex !== -1) {
			newModifiedRows[existingIndex] = {
				...newModifiedRows[existingIndex],
				Comments: event.target.value,
			};
			setFormInstanceModifiedRows([...newModifiedRows]);
		} else {
			let newInstance = filterInstanceByQuestionId(questionId);
			newInstance = [
				{
					...newInstance[0],
					Comments: event.target.value,
				},
			];

			setFormInstanceModifiedRows([...newModifiedRows, ...newInstance]);
		}
	};

	const open = Boolean(anchorEl);
	const id = open ? "multiline-textfield-popover" : undefined;

	useEffect(() => {
		if (
			rowQuestion &&
			rowQuestion.question &&
			rowQuestion.question[1] &&
			rowQuestion.question[1][0] &&
			rowQuestion.question[1][0].Comments
		) {
			setComment(rowQuestion.question[1][0].Comments);
		} else {
			setComment("");
		}
	}, [rowQuestion.question[1][0].Comments]);

	const sortedOptions = useMemo(() => {
		return rowQuestion.question[1] as FormInstanceRowExtended[];
	}, [rowQuestion.question[1]]);

	return (
		<Grid2 container className={classes.root} alignItems="center">
			<Grid2 style={{ paddingRight: "10px" }} size={{ xs: 12, md: 3 }}>
				<label
					className={`${classes.questionText} ${
						rowQuestion.question[1][0].IsRequired
							? classes.questionTextRequired
							: ""
					}`}
				>
					{`${rowQuestion.question[1][0].QuestionText} ${
						rowQuestion.question[1][0].IsRequired ? "(*)" : ""
					}`}
				</label>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 9 }}>
				<FormControl component="fieldset" fullWidth>
					<RadioGroup
						row
						aria-labelledby="demo-row-radio-buttons-group-label"
						name={`${rowQuestion.question[1][0].QuestionId}-row-radio-buttons-group`}
						value={selectedOption}
						onChange={(event) => handleOptionChange(event.target.value)}
						className={classes.radioGroup}
					>
						{sortedOptions.map((q: FormInstanceRowExtended) => (
							<FormControlLabel
								key={`${q.QuestionId}-${q.OptionId}`}
								value={q.OptionId}
								control={
									<Radio
										size="small"
										color={
											(q.OptionTypeCode ?? "O") !== "O"
												? "secondary"
												: "primary"
										}
									/>
								}
								label={
									<span
										className={classes.optionLabel}
										style={getOptionStyle(
											q.OptionTypeCode ?? "O",
											selectedOption === q.OptionId,
										)}
									>
										{q.OptionText}
									</span>
								}
								disabled={!(q.IsEnabled ?? true) || formInstanceStatus !== "O"}
							/>
						))}
						<IconButton
							disabled={!selectedOption}
							aria-describedby={id}
							onClick={handleClick}
						>
							<CommentIcon
								style={comment ? { color: "green" } : { color: "lightgrey" }}
							/>
						</IconButton>
					</RadioGroup>
				</FormControl>
			</Grid2>
			<Popover
				id={id}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
			>
				<TextField
					disabled={formInstanceStatus !== "O"}
					multiline
					minRows={6}
					variant="outlined"
					fullWidth
					placeholder="Enter the comments here"
					onChange={handleCommentChange}
					value={comment}
					style={{ width: "500px" }}
					className={classes.commentField}
				/>
			</Popover>
		</Grid2>
	);
};
